<template>
  <div :id="id"/>
</template>

<script setup>
import Highcharts from "@/highcharts"
import colors from "@/colors"
import { onMounted } from "vue"

const props = defineProps({
  data: { type: Array, required: true },
})

const id = "lsat_distribution"

onMounted(() => {
  const admissions = props.data
  const color = colors.blue[400]

  const addWhiskers = function (chart) {
    // Make middle 50% LSAT bands
    // defaults
    const corner = 0
    const rHeight = 4
    const space = 40
    let lastPosition = 300 + space
    const stroke = 1
    const strokeColor = colors.blue[300]
    const fill = colors.blue[300]
    const textColor = colors.blue[900]
    const zInd = 3

    for (let i = 0; i < admissions.length; i++) {
      const s = admissions[i]
      const L_25 = s.lsat_25_All
      const L_50 = s.lsat_50_All
      const L_75 = s.lsat_75_All
      const addText = `Fall ${s.admission_year}`

      let goOutside

      if ((L_75 - L_25) > 30) {
        goOutside = 0
      } else {
        goOutside = 10 * (L_75 - L_25)
      }

      if (L_75 > 171) {
        goOutside = goOutside * -1.75
      }

      const bandWidth = chart.xAxis[0].toPixels(L_75) - chart.xAxis[0].toPixels(L_25)

      lastPosition = lastPosition - space

      chart.renderer.rect(chart.xAxis[0].toPixels(L_25), lastPosition, bandWidth, rHeight, corner).attr({ "stroke-width": stroke, "stroke": strokeColor, fill, "zIndex": zInd }).add()
      chart.renderer.text(addText, chart.xAxis[0].toPixels(L_25) + 2 + goOutside, lastPosition + 8).css({ color: textColor, fontWeight: "500", fontSize: "14px" }).attr({ zIndex: zInd + 1 }).add()

      chart.renderer.rect(chart.xAxis[0].toPixels(L_25), lastPosition - 13, 2, 30, corner).attr({ "stroke-width": stroke, "stroke": strokeColor, fill, "zIndex": zInd }).add()
      chart.renderer.rect(chart.xAxis[0].toPixels(L_75), lastPosition - 13, 2, 30, corner).attr({ "stroke-width": stroke, "stroke": strokeColor, fill, "zIndex": zInd }).add()

      chart.renderer.rect(chart.xAxis[0].toPixels(L_50) - 4, lastPosition - 2, 8, 8, corner).attr({
        "stroke-width": stroke,
        "stroke": colors.blue[500],
        "fill": colors.blue[300],
        "zIndex": zInd + 10,
      }).add()
    }
  }

  const options = {
    chart: {
      type: "areaspline",
    },
    legend: {
      enabled: false,
    },
    title: {
      text: "LSAT 25th-75th percentile range<br/>on nationwide score distribution",
    },
    xAxis: {
      title: { text: "LSAT score" },
      labels: {
        formatter() {
          return this.value
        },
      },
    },
    yAxis: {
      lineColor: "transparent",
      gridLineColor: "transparent",
      title: {
        text: "",
      },
      labels: {
        formatter() {
          return ""
        },
      },
    },
    tooltip: {
      crosshairs: true,
      formatter() {
        let text = ""

        if (this.x > 151) {
          text = `<span style="font-weight:600;">LSAT score: ${this.x}</span><br> ${Math.round((1 - this.y) * 1000) / 10} percentile`
        } else {
          text = `<span style="font-weight:600;">LSAT score: ${this.x}</span><br> ${Math.round((this.y) * 1000) / 10} percentile`
        }

        return text
      },
    },
    plotOptions: {
      areaspline: {
        pointStart: 120,
        marker: {
          enabled: false,
          symbol: "circle",
          radius: 2,
          states: {
            hover: {
              enabled: true,
            },
          },
        },
      },
    },
    series: [{
      name: "LSAT Score",
      data: [0.000, 0.006, 0.007, 0.008, 0.009, 0.010, 0.012, 0.014, 0.017, 0.020, 0.023, 0.028, 0.032, 0.037, 0.045, 0.054, 0.064, 0.074, 0.087, 0.102, 0.119, 0.138, 0.160, 0.181, 0.210, 0.236, 0.263, 0.294, 0.329, 0.361, 0.395, 0.432, 0.467, 0.495, 0.458, 0.423, 0.387, 0.351, 0.317, 0.285, 0.252, 0.224, 0.195, 0.169, 0.146, 0.123, 0.104, 0.085, 0.070, 0.056, 0.044, 0.034, 0.026, 0.019, 0.014, 0.009, 0.007, 0.005, 0.003, 0.002, 0.001],
      lineColor: color,
      fillColor: `${color}20`,
    }],
  }
  new Highcharts.chart(id, options, addWhiskers)
})
</script>
